import * as React from "react"
import { router, usePage } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { InlineEditBar, InlineField } from "@/components/ui/inline-edit"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { RichTextField } from "@/components/ui/rich-text-field"
import { TagInput } from "@/components/ui/tag-input"
import { PhotoSelectField, type PhotoSelection } from "@/components/photo-library/PhotoSelectField"
import { useInlineRecord } from "@/hooks/use-inline-record"
import { inlinePatch } from "@/lib/inline-save"
import type { LibraryPhoto } from "@/components/pages-cms/types"
import type { PageProps } from "@/types/inertia"

export type TherapistData = {
  id?: number
  name: string
  slug: string
  email: string
  speciality_en: string
  speciality_es: string
  bio_en: string
  bio_es: string
  website_url: string
  status: string
  listed_at: string | null
  site_ids: number[]
  photo_url?: string | null
  specialisms: string[]
  qualifications: string[]
  accreditations: string[]
  training: string[]
  languages: string[]
  approach_cbt: string
  approach_emdr: string
  approach_closing: string
  what_to_expect: string
}

type SiteOption = { id: number; name: string }

export function TherapistForm({
  therapist,
  sites,
  actionUrl,
  method,
  photoLibrary = [],
}: {
  therapist: TherapistData
  sites: SiteOption[]
  actionUrl: string
  method: "post" | "patch"
  photoLibrary?: LibraryPhoto[]
}) {
  const { props } = usePage<PageProps>()
  const errors = props.errors ?? {}
  const isNew = method === "post"

  const fields = useInlineRecord({
    record: {
      name: therapist.name,
      slug: therapist.slug,
      email: therapist.email,
      speciality_en: therapist.speciality_en,
      speciality_es: therapist.speciality_es,
      bio_en: therapist.bio_en,
      bio_es: therapist.bio_es,
      website_url: therapist.website_url,
      status: therapist.status,
      listed_at: therapist.listed_at ?? "",
      site_ids: therapist.site_ids,
      specialisms: therapist.specialisms ?? [],
      qualifications: therapist.qualifications ?? [],
      accreditations: therapist.accreditations ?? [],
      training: therapist.training ?? [],
      languages: therapist.languages ?? [],
      approach_cbt: therapist.approach_cbt ?? "",
      approach_emdr: therapist.approach_emdr ?? "",
      approach_closing: therapist.approach_closing ?? "",
      what_to_expect: therapist.what_to_expect ?? "",
    },
    url: actionUrl,
    param: "therapist",
    enabled: !isNew,
  })
  const v = fields.values

  // The photo is a file rather than a value in the draft: on a saved profile
  // it goes up the moment it is chosen, since you can already see whether it
  // is the right one.
  const [photo, setPhoto] = React.useState<PhotoSelection>({ kind: "unchanged" })
  const [processing, setProcessing] = React.useState(false)

  function choosePhoto(selection: PhotoSelection) {
    setPhoto(selection)
    if (isNew || selection.kind === "unchanged") return
    if (selection.kind === "library") {
      inlinePatch(actionUrl, { therapist: { photo_id: selection.id } })
    } else {
      inlinePatch(actionUrl, { therapist: { photo: selection.file } }, { forceFormData: true })
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setProcessing(true)
    router.post(
      actionUrl,
      {
        therapist: {
          ...v,
          listed_at: v.listed_at || null,
          site_ids: siteIdsWire(v.site_ids),
          ...(photo.kind === "upload" ? { photo: photo.file } : {}),
          ...(photo.kind === "library" ? { photo_id: photo.id } : {}),
        },
      },
      { forceFormData: photo.kind === "upload", onFinish: () => setProcessing(false) },
    )
  }

  // An empty list has to arrive as something: Rails deep-munges [] to nil,
  // which the controller reads as "this request didn't mention locations".
  function siteIdsWire(ids: number[]) {
    return ids.length > 0 ? ids : [ "" ]
  }

  function toggleSite(id: number) {
    fields.set(
      "site_ids",
      v.site_ids.includes(id) ? v.site_ids.filter((s) => s !== id) : [ ...v.site_ids, id ],
    )
  }

  const nameState = fields.state("name")
  const emailState = fields.state("email")
  const slugState = fields.state("slug")
  const websiteState = fields.state("website_url")
  const statusState = fields.state("status")
  const listedAtState = fields.state("listed_at", { serialize: (value) => value || null })
  const specialityEnState = fields.state("speciality_en")
  const specialityEsState = fields.state("speciality_es")
  const specialismsState = fields.state("specialisms")
  const qualificationsState = fields.state("qualifications")
  const accreditationsState = fields.state("accreditations")
  const trainingState = fields.state("training")
  const languagesState = fields.state("languages")
  const bioEnState = fields.state("bio_en")
  const bioEsState = fields.state("bio_es")
  const cbtState = fields.state("approach_cbt")
  const emdrState = fields.state("approach_emdr")
  const closingState = fields.state("approach_closing")
  const expectState = fields.state("what_to_expect")
  const siteIdsState = fields.state("site_ids", { serialize: siteIdsWire })

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Basic info */}
      <section className="space-y-4">
        <h2>Basic information</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <InlineField label="Name" htmlFor="name" error={errors.name} state={nameState}>
            <Input
              id="name"
              value={v.name}
              onChange={(e) => fields.set("name", e.target.value)}
              onKeyDown={nameState.keys}
              required
              aria-invalid={!!errors.name}
            />
          </InlineField>

          <InlineField label="Email" htmlFor="email" error={errors.email} state={emailState}>
            <Input
              id="email"
              type="email"
              value={v.email}
              onChange={(e) => fields.set("email", e.target.value)}
              onKeyDown={emailState.keys}
              aria-invalid={!!errors.email}
            />
          </InlineField>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <InlineField label="URL slug" htmlFor="slug" error={errors.slug} state={slugState}>
            <Input
              id="slug"
              value={v.slug}
              onChange={(e) => fields.set("slug", e.target.value)}
              onKeyDown={slugState.keys}
              placeholder="auto-generated from name"
              aria-invalid={!!errors.slug}
            />
          </InlineField>

          <InlineField
            label="Website URL"
            htmlFor="website_url"
            error={errors.website_url}
            state={websiteState}
          >
            <Input
              id="website_url"
              type="url"
              value={v.website_url}
              onChange={(e) => fields.set("website_url", e.target.value)}
              onKeyDown={websiteState.keys}
              placeholder="https://..."
              aria-invalid={!!errors.website_url}
            />
          </InlineField>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <InlineField label="Status" htmlFor="status" error={errors.status} state={statusState}>
            <Select
              id="status"
              value={v.status}
              onChange={(e) => fields.set("status", e.target.value)}
              aria-invalid={!!errors.status}
            >
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="inactive">Inactive</option>
            </Select>
          </InlineField>

          <InlineField label="Listed since" htmlFor="listed_at" state={listedAtState}>
            <Input
              id="listed_at"
              type="date"
              value={v.listed_at}
              onChange={(e) => fields.set("listed_at", e.target.value)}
              onKeyDown={listedAtState.keys}
            />
          </InlineField>
        </div>
      </section>

      {/* Speciality */}
      <section className="space-y-4">
        <h2>Speciality</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <InlineField
            label="Speciality (English)"
            htmlFor="speciality_en"
            error={errors.speciality_en}
            state={specialityEnState}
          >
            <Input
              id="speciality_en"
              value={v.speciality_en}
              onChange={(e) => fields.set("speciality_en", e.target.value)}
              onKeyDown={specialityEnState.keys}
              placeholder="e.g. Anxiety & Depression"
            />
          </InlineField>

          <InlineField
            label="Speciality (Spanish)"
            htmlFor="speciality_es"
            error={errors.speciality_es}
            state={specialityEsState}
          >
            <Input
              id="speciality_es"
              value={v.speciality_es}
              onChange={(e) => fields.set("speciality_es", e.target.value)}
              onKeyDown={specialityEsState.keys}
              placeholder="e.g. Ansiedad y Depresión"
            />
          </InlineField>
        </div>
      </section>

      {/* Tags */}
      <section className="space-y-4">
        <h2>Tags</h2>
        <InlineField
          label="Specialisms"
          hint="Type a specialism and press Enter or comma to add. These appear as pills on the therapy page."
          error={errors.specialisms}
          state={specialismsState}
        >
          <TagInput
            value={v.specialisms}
            onChange={(next) => fields.set("specialisms", next)}
            placeholder="e.g. OCD, Anxiety, Trauma…"
          />
        </InlineField>

        <InlineField
          label="Qualifications"
          hint="Degrees and diplomas. Type one and press Enter or comma to add."
          error={errors.qualifications}
          state={qualificationsState}
        >
          <TagInput
            value={v.qualifications}
            onChange={(next) => fields.set("qualifications", next)}
            placeholder="e.g. MSc Cognitive Behavioural Therapy (Distinction)…"
          />
        </InlineField>

        <InlineField
          label="Accreditations"
          hint="Registrations and accredited memberships, shown separately from qualifications."
          error={errors.accreditations}
          state={accreditationsState}
        >
          <TagInput
            value={v.accreditations}
            onChange={(next) => fields.set("accreditations", next)}
            placeholder="e.g. BABCP Accredited, EMDR Europe Practitioner…"
          />
        </InlineField>

        <InlineField
          label="Training & CPD"
          hint="Ongoing courses and continuing professional development."
          error={errors.training}
          state={trainingState}
        >
          <TagInput
            value={v.training}
            onChange={(next) => fields.set("training", next)}
            placeholder="e.g. Inference-based CBT for OCD, 3-day course (2025)…"
          />
        </InlineField>

        <InlineField
          label="Languages"
          hint="Type a language and press Enter or comma to add."
          error={errors.languages}
          state={languagesState}
        >
          <TagInput
            value={v.languages}
            onChange={(next) => fields.set("languages", next)}
            placeholder="e.g. English, Spanish…"
          />
        </InlineField>
      </section>

      {/* Bio */}
      <section className="space-y-4">
        <h2>Bio</h2>
        <InlineField label="Bio (English)" error={errors.bio_en} state={bioEnState}>
          <RichTextField
            key={`bio-en-${bioEnState.version}`}
            defaultValue={bioEnState.value}
            onChange={(md) => fields.set("bio_en", md)}
            placeholder="Write the therapist's bio in English…"
          />
        </InlineField>
        <InlineField label="Bio (Spanish)" error={errors.bio_es} state={bioEsState}>
          <RichTextField
            key={`bio-es-${bioEsState.version}`}
            defaultValue={bioEsState.value}
            onChange={(md) => fields.set("bio_es", md)}
            placeholder="Escriba la biografía del terapeuta en español…"
          />
        </InlineField>
      </section>

      {/* Approach */}
      <section className="space-y-4">
        <h2>Approach</h2>
        <InlineField label="CBT approach" error={errors.approach_cbt} state={cbtState}>
          <RichTextField
            key={`cbt-${cbtState.version}`}
            defaultValue={cbtState.value}
            onChange={(md) => fields.set("approach_cbt", md)}
            placeholder="Describe the CBT approach…"
          />
        </InlineField>
        <InlineField label="EMDR approach" error={errors.approach_emdr} state={emdrState}>
          <RichTextField
            key={`emdr-${emdrState.version}`}
            defaultValue={emdrState.value}
            onChange={(md) => fields.set("approach_emdr", md)}
            placeholder="Describe the EMDR approach…"
          />
        </InlineField>
        <InlineField label="Closing statement" error={errors.approach_closing} state={closingState}>
          <RichTextField
            key={`closing-${closingState.version}`}
            defaultValue={closingState.value}
            onChange={(md) => fields.set("approach_closing", md)}
            placeholder="Personalisation statement to close the approach section…"
          />
        </InlineField>
      </section>

      {/* What to expect */}
      <section className="space-y-4">
        <h2>What to expect</h2>
        <InlineField label="What to expect content" error={errors.what_to_expect} state={expectState}>
          <RichTextField
            key={`expect-${expectState.version}`}
            defaultValue={expectState.value}
            onChange={(md) => fields.set("what_to_expect", md)}
            placeholder="Describe what clients can expect from sessions…"
          />
        </InlineField>
      </section>

      {/* Photo */}
      <section className="space-y-4">
        <h2>Photo</h2>
        <div className="space-y-1.5">
          <label htmlFor="photo">Profile photo</label>
          <PhotoSelectField
            library={photoLibrary}
            currentUrl={therapist.photo_url}
            value={photo}
            onChange={choosePhoto}
            shape="avatar"
            inputId="photo"
          />
          {!isNew && <p className="text-xs text-ink-muted">Choosing a photo saves it straight away.</p>}
        </div>
      </section>

      {/* Locations */}
      {sites.length > 0 && (
        <section className="space-y-4">
          <h2>Locations</h2>
          <div className="space-y-2">
            {sites.map((site) => (
              <div key={site.id} className="flex items-center gap-2">
                <Checkbox
                  id={`site-${site.id}`}
                  checked={v.site_ids.includes(site.id)}
                  onChange={() => toggleSite(site.id)}
                />
                <label htmlFor={`site-${site.id}`} className="font-normal text-ink-body">
                  {site.name}
                </label>
              </div>
            ))}
          </div>
          {errors.site_ids && <p className="text-xs text-danger-display">{errors.site_ids}</p>}
          {/* The whole list is one answer, so it commits as one. */}
          <InlineEditBar state={siteIdsState} />
        </section>
      )}

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-3 border-t border-hairline pt-6">
        {isNew ? (
          <>
            <Button type="submit" disabled={processing}>
              Add therapist
            </Button>
            <Button type="button" variant="ghost" onClick={() => window.history.back()}>
              Cancel
            </Button>
          </>
        ) : (
          <p className="text-sm text-ink-muted">
            Each change is kept when you tick it — there's nothing else to save.
          </p>
        )}
      </div>
    </form>
  )
}
